import { useEffect, useState } from "react";
import { RotateCw, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { STATUS_COLORS, withAlpha, BRAND_COLORS } from "../lib/bookingStatus";
import { deDateMedium, deWeekdayDateShortTime } from "../lib/dateFormat";

/**
 * Team dashboard log-card for the weekly status-cleanup job.
 * - Shows the latest run (Sunday-night automated OR manual trigger)
 * - Lists the last 5 runs so the admin can spot drift patterns
 * - "Jetzt prüfen"-Button triggers the sync endpoint and refreshes
 *
 * The endpoint is admin/concierge-only — this card is only mounted from the
 * team dashboard, so no role gate needed here.
 */
function relativeDe(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "gerade eben";
    if (mins < 60) return `vor ${mins} Min.`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
    return deDateMedium(d);
  } catch { return iso; }
}

function fmtStampDe(iso) {
  if (!iso) return "—";
  try {
    return deWeekdayDateShortTime(iso);
  } catch { return iso; }
}

export default function StatusCleanupCard() {
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/team/status-cleanup-log", { params: { limit: 5 } });
      setLog(data);
    } catch (e) {
      console.error("status-cleanup log load failed:", e);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/team/recompute-booking-statuses");
      const corrected = data?.corrected ?? 0;
      toast.success(
        corrected === 0
          ? `Prüfung abgeschlossen · ${data.checked} Buchungen · alles konsistent.`
          : `Prüfung abgeschlossen · ${corrected} Buchung(en) korrigiert.`,
      );
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Prüfung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  const latest = log?.runs?.[0] || null;
  const totalCorrected = (log?.runs || []).reduce((a, r) => a + (r.corrected || 0), 0);
  const hasIssue = latest && (latest.corrected || 0) > 0;

  return (
    <div className="bg-white border border-navy/10 p-6 md:p-7" data-testid="status-cleanup-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2" style={{ color: BRAND_COLORS.gold }}>
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} />
            Wöchentliche Statuskorrektur
          </div>
          <h2 className="font-display text-2xl text-navy">Selbstheilung des Buchungs-Status</h2>
          <p className="text-[13px] text-navy-dark/70 mt-2 max-w-2xl leading-relaxed">
            Jeden <strong>Montag um 02:00 UTC</strong> prüft ein automatischer Job alle aktiven Buchungen und
            korrigiert falsch als „Erledigt" markierte Bookings auf „In Betreuung",
            falls Punkte noch offen sind. Ergebnisse werden hier protokolliert.
          </p>
        </div>
        <button
          data-testid="status-cleanup-run-btn"
          onClick={runNow}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-2.5 text-[11.5px] tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-colors disabled:opacity-40"
        >
          <RotateCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} strokeWidth={2} />
          {busy ? "Prüfe…" : "Jetzt prüfen"}
        </button>
      </div>

      {latest ? (
        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5 p-4"
          style={{
            background: hasIssue ? withAlpha(STATUS_COLORS.red, 0.04) : withAlpha(STATUS_COLORS.green, 0.04),
            borderLeft: `3px solid ${hasIssue ? STATUS_COLORS.red : STATUS_COLORS.green}`,
          }}
          data-testid="status-cleanup-latest"
        >
          <div>
            <div className="label-caps mb-1" style={{ color: hasIssue ? STATUS_COLORS.red : STATUS_COLORS.green }}>
              Letzter Lauf
            </div>
            <div className="font-display text-lg text-navy" data-testid="status-cleanup-latest-when">
              {fmtStampDe(latest.started_at)}
            </div>
            <div className="text-[11px] text-navy-dark/55 mt-0.5">
              {relativeDe(latest.started_at)} · {latest.source === "cron" ? "automatisch" : "manuell"}
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Geprüft</div>
            <div className="font-display text-2xl text-navy" data-testid="status-cleanup-latest-checked">
              {latest.checked}
            </div>
            <div className="text-[11px] text-navy-dark/55 mt-0.5">Buchungen (aktiv)</div>
          </div>
          <div>
            <div className="label-caps mb-1" style={{ color: hasIssue ? STATUS_COLORS.red : STATUS_COLORS.green }}>
              Korrigiert
            </div>
            <div
              className="font-display text-2xl flex items-center gap-2"
              style={{ color: hasIssue ? STATUS_COLORS.red : STATUS_COLORS.green }}
              data-testid="status-cleanup-latest-corrected"
            >
              {latest.corrected}
              {hasIssue
                ? <AlertTriangle className="w-4 h-4" strokeWidth={2} />
                : <CheckCircle2 className="w-4 h-4" strokeWidth={2} />}
            </div>
            <div className="text-[11px] text-navy-dark/55 mt-0.5">
              {hasIssue ? "wieder in Betreuung" : "alles konsistent"}
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Dauer</div>
            <div className="font-display text-lg text-navy">
              {(latest.duration_ms || 0)} ms
            </div>
            <div className="text-[11px] text-navy-dark/55 mt-0.5">Server-Zeit</div>
          </div>
        </div>
      ) : (
        <div
          className="mb-5 p-4 text-[13px] text-navy-dark/60 border border-dashed border-navy/15"
          data-testid="status-cleanup-empty"
        >
          Noch keine Läufe protokolliert. Der erste automatische Lauf ist Montag 02:00 UTC —
          bis dahin kann jederzeit manuell geprüft werden.
        </div>
      )}

      {log?.runs?.length > 1 && (
        <div>
          <div className="label-caps mb-2">Vergangene Läufe</div>
          <ul className="divide-y divide-navy/5" data-testid="status-cleanup-history">
            {log.runs.slice(1).map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between py-2.5 text-[13px]"
                data-testid={`status-cleanup-row-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-navy truncate">{fmtStampDe(r.started_at)}</div>
                  <div className="text-[11px] text-navy-dark/50">
                    {r.source === "cron" ? "automatisch" : "manuell"} · {relativeDe(r.started_at)}
                  </div>
                </div>
                <div className="flex items-center gap-4 tabular-nums shrink-0">
                  <span className="text-navy-dark/60">
                    {r.checked} gepr.
                  </span>
                  <span
                    className="font-medium"
                    style={{ color: (r.corrected || 0) > 0 ? STATUS_COLORS.red : STATUS_COLORS.green }}
                  >
                    {r.corrected || 0} korr.
                  </span>
                  <span className="text-[11px] text-navy-dark/40 w-14 text-right">
                    {r.duration_ms || 0} ms
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-[11px] text-navy-dark/50 mt-4 pt-4 border-t border-navy/5">
        Kumulativ korrigiert (letzte {log?.runs?.length || 0} Läufe): <strong>{totalCorrected}</strong>
      </p>
    </div>
  );
}
